/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Avatar, Button, Col, Form, Input, Row, Select, Typography } from 'antd';

// Misc imports
import is from '../../utils/is';
import { useCloneBotMutation } from '../../store/bots/botsApi';
import OrgPlaceholderImage from '../../assets/placeholder_organization.svg';
import { NAV_BOT } from '../../constants/navigation';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { updateRecentBots } from '../../store/bots/botsActions';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const { Option } = Select;
const { Text } = Typography;

const BotCloneForm = ({
  botToClone,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { currentUser } = useSelector((state) => state.auth);
  const [cloneBot, { isLoading: isCreatingBot, reset: resetCloneBot }] = useCloneBotMutation();

  useEffect(() => {
    // Reset data on bot change
    return () => form.resetFields();
  }, [botToClone]);

  const onSubmit = async (values) => {
    let valuesToSubmit = {
      displayName: values.displayName
    };

    const newId = await cloneBot({ orgId, tenantId: botToClone.id, targetOrgId: values.orgId, data: valuesToSubmit, successMessage: { showMessage: false } }).unwrap();

    if (newId) {
      resetCloneBot();
      // After clone is done, fetch the latest data
      openConsoleAlertMessage({
        type: 'success',
        message: t('Agent cloned!'),
        duration: 10,
        secondaryButton: {
          action: () => {
            if (orgId !== values.orgId) {
              dispatch(updateRecentBots([]));
            }
            navigate(`${replaceUrlParams(NAV_BOT, { orgId: values.orgId, tenantId: newId })}`);
          },
          title: t('View')
        }
      });

      // Trigger callback to notify parent component of success
      onSuccess(valuesToSubmit);
    } else {
      openConsoleAlertMessage({ type: 'error', message: t('There was an error cloning the agent.') });
    }
  };

  return botToClone && (
    <Row gutter={24}>
      <Col xs={24}>
        <Form
          wrapperCol={{ span: 24 }}
          name="BotCloneForm"
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{
            orgId: orgId,
            displayName: `${botToClone.displayName} (Copy)`,
            id: botToClone.id
          }}
          form={form}
          requiredMark={false}
        >
          <Row gutter={16} className="mb-3">
            <Col xs={24}>
              <Form.Item
                name="orgId"
                label={
                  <Row>
                    <Col span={24}>
                      <Text>
                        {t('Workspace')}
                      </Text>
                    </Col>
                    <Col span={24} className="mb-1">
                      <Text type="secondary">
                        {t('The cloned agent will be added to the chosen Workspace')}.
                      </Text>
                    </Col>
                  </Row>
                }
                hidden={is.array(currentUser.organizations) && currentUser.organizations.length === 1}
                rules={[{
                  required: true,
                  message: `${t('Please select a Workspace')}.`
                }]}
              >
                <Select
                  placeholder={t('Choose a Workspace')}
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    is.array(currentUser.organizations) && currentUser.organizations.find(org => org.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {
                    is.array(currentUser.organizations) ? (
                      currentUser.organizations.map(org => (
                        <Option key={org.id} value={org.id}>
                          <Avatar
                            size={24}
                            src={org.image ?? OrgPlaceholderImage}
                            className="mr-2 overflow-visible"
                          />
                          {org.displayName}
                        </Option>
                      ))
                    ) : null
                  }
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="displayName"
                label={
                  <Row>
                    <Col span={24}>
                      <Text>
                        {t('Name')}
                      </Text>
                    </Col>
                    <Col span={24} className="mb-1">
                      <Text type="secondary">
                        {t('The display name for the cloned agent')}.
                      </Text>
                    </Col>
                  </Row>
                }
              >
                <Input
                  size="large"
                  autoFocus={true}
                  onFocus={(e) => e.target.select()}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isCreatingBot}
            >
              {t('Clone agent')}
            </Button>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

BotCloneForm.propTypes = {
  botToClone: PropTypes.object,
  onSuccess: PropTypes.func
};

export default BotCloneForm;
